import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Navbar from "./components/Layout/Navbar";
import MembersBar from "./components/Layout/MembersBar";
import Footer from "./components/Layout/Footer";
import ErrorBoundary from "./components/ErrorBoundary";
import PrivacyNotice from "./components/PrivacyNotice";
import SiteSchema from "./components/seo/SiteSchema";
import { useAuth } from "./context/AuthContext";
import showcaseConfig from "./config/showcases.json";

const MEMBER_TIERS = new Set(["beta", "paid", "lifetime"]);
const SHOWCASES = showcaseConfig.showcases || [];
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

import Landing from "./pages/Landing";
import TodaysCard from "./pages/TodaysCard";

const TrainerExplorer = lazy(() => import("./pages/TrainerExplorer"));
const JockeyExplorer = lazy(() => import("./pages/JockeyExplorer"));
const BetCalculator = lazy(() => import("./pages/BetCalculator"));
const DrawdownCalculator = lazy(() => import("./pages/DrawdownCalculator"));
const ArticleHome = lazy(() => import("./pages/ArticleHome"));
const TailsNeverFails = lazy(() => import("./pages/TailsNeverFails"));
const KellyGuide = lazy(() => import("./pages/KellyGuide"));
const DrawdownGuide = lazy(() => import("./pages/DrawdownGuide"));
const PriceIsRight = lazy(() => import("./pages/PriceIsRight"));
const EdgeDecay = lazy(() => import("./pages/EdgeDecay"));
const TissueKellyGuide = lazy(() => import("./pages/TissueKellyGuide"));
const RacingEthics = lazy(() => import("./pages/RacingEthics"));
const InstinctGap = lazy(() => import("./pages/InstinctGap"));
const TrainerIntent = lazy(() => import("./pages/TrainerIntent"));
const AvoidFlag = lazy(() => import("./pages/AvoidFlag"));
const RaceRadarExplorer = lazy(() => import("./pages/RaceRadarExplorer"));
const PaceMapExplorer = lazy(() => import("./pages/PaceMapExplorer"));
const DrawBiasExplorer = lazy(() => import("./pages/DrawBiasExplorer"));
const TissueExplorer = lazy(() => import("./pages/TissueExplorer"));
const MemberTissue = lazy(() => import("./pages/MemberTissue"));
const MemberGoingBias = lazy(() => import("./pages/MemberGoingBias"));
const MemberTrainerStats = lazy(() => import("./pages/MemberTrainerStats"));
const MemberBreeding = lazy(() => import("./pages/MemberBreeding"));
const MemberTopStrongest = lazy(() => import("./pages/MemberTopStrongest"));
const MemberTopStrongestBacktest = lazy(() => import("./pages/MemberTopStrongestBacktest"));
const MemberTissueValidation = lazy(() => import("./pages/MemberTissueValidation"));
const MemberTissueNh = lazy(() => import("./pages/MemberTissueNh"));
const MemberTissueAw = lazy(() => import("./pages/MemberTissueAw"));
const MemberBreedingAw = lazy(() => import("./pages/MemberBreedingAw"));
const MemberTrainerStatsAw = lazy(() => import("./pages/MemberTrainerStatsAw"));
const MemberCourseBiasAw = lazy(() => import("./pages/MemberCourseBiasAw"));
const MemberFormGraphs = lazy(() => import("./pages/MemberFormGraphs"));
const MemberPicks = lazy(() => import("./pages/MemberPicks"));
const MemberTrackRecord = lazy(() => import("./pages/MemberTrackRecord"));
const MemberRoute = lazy(() => import("./components/MemberRoute"));
const MyTracker = lazy(() => import("./pages/MyTracker"));
const TrackerUnsubscribe = lazy(() => import("./pages/TrackerUnsubscribe"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const MemberTissueRecent = lazy(() => import("./pages/MemberTissueRecent"));
const GoingBiasBacktestExplorer = lazy(() => import("./pages/GoingBiasBacktestExplorer"));
const DrawBiasBacktestExplorer = lazy(() => import("./pages/DrawBiasBacktestExplorer"));
// const AWPicksExplorer = lazy(() => import("./pages/AWPicksExplorer")); // dev only, not on master
const Showcase = lazy(() => import("./pages/Showcase"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const MispricedDownloads = lazy(() => import("./pages/MispricedDownloads"));
const BookSynopsis = lazy(() => import("./pages/BookSynopsis"));
const AmbassadorialCaseStudy = lazy(() => import("./pages/AmbassadorialCaseStudy"));
const ShishkinCaseStudy = lazy(() => import("./pages/ShishkinCaseStudy"));
const DeBromheadCaseStudy = lazy(() => import("./pages/DeBromheadCaseStudy"));
const JohnstonCaseStudy = lazy(() => import("./pages/JohnstonCaseStudy"));
const BaldingCaseStudy = lazy(() => import("./pages/BaldingCaseStudy"));
const BSPBandCaseStudy = lazy(() => import("./pages/BSPBandCaseStudy"));
const IAmMaximusCaseStudy = lazy(() => import("./pages/IAmMaximusCaseStudy"));
const BarneyCurleyCaseStudy = lazy(() => import("./pages/BarneyCurleyCaseStudy"));
const LongshotBias = lazy(() => import("./pages/LongshotBias"));
const EquipmentChangesDataStudy = lazy(() => import("./pages/EquipmentChangesDataStudy"));
const NHPipelineSeasonBySeason = lazy(() => import("./pages/NHPipelineSeasonBySeason"));
const ExperimentsWhatFailed = lazy(() => import("./pages/ExperimentsWhatFailed"));
const GoingBiasFromScratch = lazy(() => import("./pages/GoingBiasFromScratch"));
const HeadgearTracking = lazy(() => import("./pages/HeadgearTracking"));
const RatingsFrameworkWorkbook = lazy(() => import("./pages/RatingsFrameworkWorkbook"));
const CalibrationInPractice = lazy(() => import("./pages/CalibrationInPractice"));
const WalkForwardValidation = lazy(() => import("./pages/WalkForwardValidation"));
const BootstrapCIMethodology = lazy(() => import("./pages/BootstrapCIMethodology"));
const HowToSpotOverfittedSystem = lazy(() => import("./pages/HowToSpotOverfittedSystem"));
const OpinionHub = lazy(() => import("./pages/OpinionHub"));
const AnalysisHub = lazy(() => import("./pages/AnalysisHub"));
const CaseStudiesHub = lazy(() => import("./pages/CaseStudiesHub"));
const ResearchHub = lazy(() => import("./pages/ResearchHub"));
const TheLabHub = lazy(() => import("./pages/TheLabHub"));
const PriceOfProtection = lazy(() => import("./pages/PriceOfProtection"));
const AffordabilityChecksLetter = lazy(() => import("./pages/AffordabilityChecksLetter"));
const RestrictionMagicTrick = lazy(() => import("./pages/RestrictionMagicTrick"));
const SelectiveCertainty = lazy(() => import("./pages/SelectiveCertainty"));
const InfluencerTipster = lazy(() => import("./pages/InfluencerTipster"));
const NonRunnerThatRan = lazy(() => import("./pages/NonRunnerThatRan"));
const TheSameTell = lazy(() => import("./pages/TheSameTell"));
const GateClosesForStrangers = lazy(() => import("./pages/GateClosesForStrangers"));
const RulesThatServeTheRulemaker = lazy(() => import("./pages/RulesThatServeTheRulemaker"));
const BacktestsHub = lazy(() => import("./pages/BacktestsHub"));
const MemberTissueHub = lazy(() => import("./pages/MemberTissueHub"));
const MemberGoingBiasHub = lazy(() => import("./pages/MemberGoingBiasHub"));
const MemberGoingBiasNh = lazy(() => import("./pages/MemberGoingBiasNh"));
const MemberBreedingHub = lazy(() => import("./pages/MemberBreedingHub"));
const MemberBreedingNh = lazy(() => import("./pages/MemberBreedingNh"));
const MemberTrainerStatsHub = lazy(() => import("./pages/MemberTrainerStatsHub"));
const MemberTrainerStatsNh = lazy(() => import("./pages/MemberTrainerStatsNh"));
const MemberHelp = lazy(() => import("./pages/MemberHelp"));
const HelpHub = lazy(() => import("./pages/HelpHub"));
const TissuePricerGuide = lazy(() => import("./pages/TissuePricerGuide"));
const TodaysCardsGuide = lazy(() => import("./pages/TodaysCardsGuide"));
const TrainerHeatmapGuide = lazy(() => import("./pages/TrainerHeatmapGuide"));
const JockeyHeatmapGuide = lazy(() => import("./pages/JockeyHeatmapGuide"));
const DrawBiasGuide = lazy(() => import("./pages/DrawBiasGuide"));
const RaceRadarGuide = lazy(() => import("./pages/RaceRadarGuide"));
const NLQueryPage = lazy(() => import("./pages/nl_query/NLQueryPage"));
const AskTheNotebookGuide = lazy(() => import("./pages/nl_query/AskTheNotebookGuide"));
const RacePreviews = lazy(() => import("./pages/RacePreviews"));
const NotFound = lazy(() => import("./pages/NotFound"));

function Home() {
  const isMobile = window.matchMedia("(max-width: 768px)").matches;
  if (isMobile) return <Navigate to="/tools/todays-card" replace />;
  return <Landing />;
}

function AppShell() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isMember = !!user && MEMBER_TIERS.has(user.subscription_tier);
  return (
    <>
      <SiteSchema />
      <ScrollToTop />
      <Navbar />
      <MembersBar />
      <main
        className={isMember ? "has-members-bar" : undefined}
        style={{ minHeight: "calc(100vh - 160px)" }}
      >
        {/* Contain any page-render throw to a card — the navbar/footer chrome
            survives. resetKey={pathname} clears the error on navigation. */}
        <ErrorBoundary resetKey={pathname}>
        <Suspense fallback={<div style={{ padding: "2rem", textAlign: "center" }}>Loading…</div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/articles" element={<ArticleHome />} />
            <Route path="/articles/opinion" element={<OpinionHub />} />
            <Route path="/articles/tails-never-fails" element={<TailsNeverFails />} />
            <Route path="/articles/kelly-guide" element={<KellyGuide />} />
            <Route path="/articles/drawdown-bank-sizing" element={<DrawdownGuide />} />
            <Route path="/articles/the-price-is-right" element={<PriceIsRight />} />
            <Route path="/articles/edge-decay" element={<EdgeDecay />} />
            <Route path="/articles/tissue-kelly-guide" element={<TissueKellyGuide />} />
            <Route path="/articles/racing-ethics" element={<RacingEthics />} />
            <Route path="/articles/price-of-protection" element={<PriceOfProtection />} />
            <Route path="/articles/affordability-checks-letter" element={<AffordabilityChecksLetter />} />
            <Route path="/articles/the-restriction-magic-trick" element={<RestrictionMagicTrick />} />
            <Route path="/articles/selective-certainty" element={<SelectiveCertainty />} />
            <Route path="/articles/influencer-tipster" element={<InfluencerTipster />} />
            <Route path="/articles/non-runner-that-ran" element={<NonRunnerThatRan />} />
            <Route path="/articles/the-same-tell" element={<TheSameTell />} />
            <Route path="/articles/gate-only-closes-for-strangers" element={<GateClosesForStrangers />} />
            <Route path="/articles/rules-that-serve-the-rulemaker" element={<RulesThatServeTheRulemaker />} />
            <Route path="/articles/the-instinct-gap" element={<InstinctGap />} />
            <Route path="/articles/trainer-intent" element={<TrainerIntent />} />
            <Route path="/articles/the-avoid-flag" element={<AvoidFlag />} />
            <Route path="/articles/ambassadorial-case-study" element={<AmbassadorialCaseStudy />} />
            <Route path="/articles/shishkin-case-study" element={<ShishkinCaseStudy />} />
            <Route path="/articles/de-bromhead-case-study" element={<DeBromheadCaseStudy />} />
            <Route path="/articles/johnston-case-study" element={<JohnstonCaseStudy />} />
            <Route path="/articles/balding-case-study" element={<BaldingCaseStudy />} />
            <Route path="/articles/bsp-bands" element={<BSPBandCaseStudy />} />
            <Route path="/articles/i-am-maximus-case-study" element={<IAmMaximusCaseStudy />} />
            <Route path="/articles/barney-curley-case-study" element={<BarneyCurleyCaseStudy />} />
            <Route path="/articles/longshot-bias" element={<LongshotBias />} />
            <Route path="/articles/equipment-changes-study" element={<EquipmentChangesDataStudy />} />
            <Route path="/articles/nhpipeline-season-by-season" element={<NHPipelineSeasonBySeason />} />
            <Route path="/articles/experiments-what-failed" element={<ExperimentsWhatFailed />} />
            <Route path="/articles/going-bias-from-scratch" element={<GoingBiasFromScratch />} />
            <Route path="/articles/headgear-tracking" element={<HeadgearTracking />} />
            <Route path="/articles/ratings-framework-workbook" element={<RatingsFrameworkWorkbook />} />
            <Route path="/articles/calibration-in-practice" element={<CalibrationInPractice />} />
            <Route path="/articles/walk-forward-validation" element={<WalkForwardValidation />} />
            <Route path="/articles/bootstrap-ci-methodology" element={<BootstrapCIMethodology />} />
            <Route path="/articles/how-to-spot-overfitted-betting-system" element={<HowToSpotOverfittedSystem />} />
            <Route path="/tools/todays-card" element={<TodaysCard />} />
            <Route path="/tools/trainer" element={<TrainerExplorer />} />
            <Route path="/tools/jockey" element={<JockeyExplorer />} />
            <Route path="/tools/race-radar" element={<RaceRadarExplorer />} />
            <Route path="/tools/pace-map" element={<PaceMapExplorer />} />
            <Route path="/tools/draw-bias" element={<DrawBiasExplorer />} />
            <Route path="/tools/tissue" element={<TissueExplorer />} />
            <Route path="/help" element={<HelpHub />} />
            <Route path="/help/tissue-pricer" element={<TissuePricerGuide />} />
            <Route path="/help/todays-cards" element={<TodaysCardsGuide />} />
            <Route path="/help/trainer-heatmap" element={<TrainerHeatmapGuide />} />
            <Route path="/help/jockey-heatmap" element={<JockeyHeatmapGuide />} />
            <Route path="/help/draw-bias" element={<DrawBiasGuide />} />
            <Route path="/help/race-radar" element={<RaceRadarGuide />} />
            <Route path="/member/form-graphs" element={<MemberRoute><MemberFormGraphs /></MemberRoute>} />
            <Route path="/member/tissue" element={<MemberRoute><MemberTissueHub /></MemberRoute>} />
            <Route path="/member/tissue/flat" element={<MemberRoute><MemberTissue /></MemberRoute>} />
            <Route path="/member/going-bias" element={<MemberRoute><MemberGoingBiasHub /></MemberRoute>} />
            <Route path="/member/going-bias/flat" element={<MemberRoute><MemberGoingBias /></MemberRoute>} />
            <Route path="/member/going-bias/nh" element={<MemberRoute><MemberGoingBiasNh /></MemberRoute>} />
            <Route path="/member/going-bias/aw" element={<MemberRoute><MemberCourseBiasAw /></MemberRoute>} />
            <Route path="/member/trainer-stats" element={<MemberRoute><MemberTrainerStatsHub /></MemberRoute>} />
            <Route path="/member/trainer-stats/flat" element={<MemberRoute><MemberTrainerStats /></MemberRoute>} />
            <Route path="/member/trainer-stats/nh" element={<MemberRoute><MemberTrainerStatsNh /></MemberRoute>} />
            <Route path="/member/trainer-stats/aw" element={<MemberRoute><MemberTrainerStatsAw /></MemberRoute>} />
            <Route path="/member/breeding" element={<MemberRoute><MemberBreedingHub /></MemberRoute>} />
            <Route path="/member/breeding/flat" element={<MemberRoute><MemberBreeding /></MemberRoute>} />
            <Route path="/member/breeding/nh" element={<MemberRoute><MemberBreedingNh /></MemberRoute>} />
            <Route path="/member/breeding/aw" element={<MemberRoute><MemberBreedingAw /></MemberRoute>} />
            <Route path="/member/today-strongest" element={<MemberRoute><MemberTopStrongest /></MemberRoute>} />
            <Route path="/member/today-strongest/backtest" element={<MemberRoute><MemberTopStrongestBacktest /></MemberRoute>} />
            {/* Tissue Validation — one tabbed page (backtest programme 03).
                The three pre-consolidation routes redirect onto their tab so
                bookmarks and the Backtests hub's old links keep working. */}
            <Route path="/member/tissue/validation" element={<MemberRoute><MemberTissueValidation /></MemberRoute>} />
            <Route path="/member/tissue/validation/:code" element={<MemberRoute><MemberTissueValidation /></MemberRoute>} />
            <Route path="/member/tissue/backtest" element={<Navigate to="/member/tissue/validation/flat" replace />} />
            <Route path="/member/tissue/nh/backtest" element={<Navigate to="/member/tissue/validation/nh" replace />} />
            <Route path="/member/tissue/nh" element={<MemberRoute><MemberTissueNh /></MemberRoute>} />
            <Route path="/member/tissue/aw" element={<MemberRoute><MemberTissueAw /></MemberRoute>} />
            <Route path="/member/tissue/aw/backtest" element={<Navigate to="/member/tissue/validation/aw" replace />} />
            <Route path="/member/model-picks" element={<MemberRoute><MemberPicks /></MemberRoute>} />
            <Route path="/member/backtests" element={<MemberRoute><BacktestsHub /></MemberRoute>} />
            <Route path="/member/tissue/recent" element={<MemberRoute><MemberTissueRecent /></MemberRoute>} />
            <Route path="/member/going-bias/recent" element={<MemberRoute><GoingBiasBacktestExplorer /></MemberRoute>} />
            <Route path="/member/draw-bias/recent" element={<MemberRoute><DrawBiasBacktestExplorer /></MemberRoute>} />
            <Route path="/member/help" element={<MemberRoute><MemberHelp /></MemberRoute>} />
            <Route path="/member/ask" element={<MemberRoute><NLQueryPage /></MemberRoute>} />
            <Route path="/member/ask/guide" element={<MemberRoute><AskTheNotebookGuide /></MemberRoute>} />
            <Route path="/track-record" element={<MemberRoute><MemberTrackRecord /></MemberRoute>} />
            <Route path="/tracker" element={<MyTracker />} />
            <Route path="/unsubscribe" element={<TrackerUnsubscribe />} />
            {/* <Route path="/tools/aw-picks" element={<AWPicksExplorer />} /> */}
            <Route path="/tools/calculator" element={<BetCalculator />} />
            <Route path="/tools/drawdown" element={<DrawdownCalculator />} />
            <Route path="/previews" element={<RacePreviews />} />
            {/* Config-driven "big race" showcases (src/config/showcases.json).
                Pretty top-level paths; one <Showcase /> component renders all.
                Adding a race is a config entry + a scraped CSV — no code here. */}
            {SHOWCASES.map((s) => (
              <Route
                key={s.slug}
                path={s.path}
                element={<Showcase slug={s.slug} />}
              />
            ))}
            <Route path="/book" element={<BookSynopsis />} />
            <Route path="/mispriced/case-studies" element={<CaseStudiesHub />} />
            <Route path="/mispriced/research" element={<ResearchHub />} />
            <Route path="/mispriced/the-lab" element={<TheLabHub />} />
            <Route path="/mispriced/analysis" element={<AnalysisHub />} />
            <Route path="/mispriced/downloads" element={<MispricedDownloads />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Catch-all — never let an unmatched path render <Routes> to null
                (a blanked site). Structural backstop behind the dead-link fixes. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        </ErrorBoundary>
      </main>
      <Footer />
      <PrivacyNotice />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}
